// Paginação prev/next pra listas cursor-based (DR-032).
// Mostra "1–20 de 34 clientes" + botões ‹ › . Não há salto direto pra página N
// — cursor pagination não suporta offset; navegação é sequencial com cache
// (voltar é instantâneo via páginas já carregadas do useInfiniteQuery).
// Documentado em DESIGN_SYSTEM.md → Componentes → Paginação.

import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PaginationProps {
  /** Índice do primeiro item visível (1-based). 0 quando lista vazia. */
  rangeStart: number;
  /** Índice do último item visível (1-based). */
  rangeEnd: number;
  /** Total de registros com os filtros atuais. null = backend não informou. */
  total: number | null;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  /** Desabilita navegação durante fetch da próxima página. */
  isLoading?: boolean;
  /** Substantivo no singular pro contador (ex: "cliente"). */
  noun?: string;
}

const NAV_BTN =
  'flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors ' +
  'hover:bg-panel-2 hover:text-ink disabled:pointer-events-none disabled:opacity-35';

export function Pagination({
  rangeStart,
  rangeEnd,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  isLoading = false,
  noun = 'registro',
}: PaginationProps): JSX.Element {
  const plural = total === 1 ? noun : `${noun}s`;
  const label =
    total === null
      ? `${rangeStart}–${rangeEnd}`
      : total === 0
        ? `Nenhum ${noun}`
        : `${rangeStart}–${rangeEnd} de ${total} ${plural}`;

  return (
    <div className="flex items-center justify-between py-3">
      <span aria-live="polite" className="text-[12px] text-muted-2">
        {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Página anterior"
          disabled={!hasPrev || isLoading}
          onClick={onPrev}
          className={NAV_BTN}
        >
          <ChevronLeft size={15} strokeWidth={2} aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Próxima página"
          disabled={!hasNext || isLoading}
          onClick={onNext}
          className={NAV_BTN}
        >
          <ChevronRight size={15} strokeWidth={2} aria-hidden />
        </button>
      </div>
    </div>
  );
}
